import { ETaskStatus, ITask } from '../../../data_models/task'
import { IProject } from '../../../data_models/project'

import { useEffect, useState } from 'react';
import { OutlinedButton, TextButton } from '../../common/Button';
import { IoCheckmark, IoClose, IoSearchOutline } from 'react-icons/io5';
import { IEmployer } from '../../../data_models/employer';
import { FreelancerRole, IFreelancer } from '../../../data_models/freelancer';
import Tooltip from '../../common/Tooltip';
import useRouting from '../../../hooks/useRouting';
import { DEFAULT_USER_PROFILE } from '../../../config/defaultAssetUrls';
import { getFreelancerName } from '../../../utils/freelancerUtils';
import { EUserType } from '../../../data_models/enums';
import useApplications from '../../../hooks/useApplications';
import useInvitations from '../../../hooks/useInvitations';
import { EInvitationStatus, Invitation } from '../../../data_models/invitation';
import { Application, EApplicationStatus } from '../../../data_models/application';
import { OutlinedDropdownMenu } from '../../common/DropdownMenu';
import { ApplicationEnumTextMapper, InvitationEnumTextMapper, UserEnumTextMapper } from '../../../data_models/enumToTextMapper';
import IconButton from '../../common/IconButton';
import ApplicationAcceptLightbox from '../../lightboxes/projects/ApplicationAcceptLighbox';
import { TaskStatusBatch } from '../../common/StatusBatch';
import ApplicationRejectLightbox from '../../lightboxes/projects/ApplicationRejectLighbox';
import InvitationWithdrawLightbox from '../../lightboxes/projects/InvitationWithdrawLightbox';
import InvitationUndoWithdrawLightbox from '../../lightboxes/projects/InvitationUndoWithdrawLightbox';
import { dateToShortReadable } from '../../../utils/dateUtils';
import { ChatNowButton } from '../../common/GhButtons';

enum EGroupBy {
    TALENT = 'Talent',
    TASK = 'Task',
}

export const ProjectDetailsTeam: React.FC<{
    project: IProject,
    tasks: ITask[],
    roadmapVerificationPm: IFreelancer | null,
    projectManager: IFreelancer | null,
    team: IFreelancer[],
    employer: IEmployer | null,
    applications: Application[],
    invitations: Invitation[],
    applicants: IFreelancer[],
    invitedFreelancers: IFreelancer[],
    isPm: boolean,
    isRoadmapVerificationPm: boolean,
    isEmployer: boolean,
    isFreelancer: boolean,
    isOutsideFreelancer: boolean,
    isOutsideEmployer: boolean,

    setProject: (project: IProject) => void,
    setTasks: (tasks: ITask[]) => void,
    setTeam: (team: IFreelancer[]) => void,
    onUpdateApplication: (application: Application) => void,
    onUpdateInvitation: (invitation: Invitation) => void,
}> = ({
    project,
    tasks,
    roadmapVerificationPm,
    projectManager,
    team,
    employer,
    applications,
    invitations,
    applicants,
    invitedFreelancers,
    isPm,
    isRoadmapVerificationPm,
    isEmployer,
    isFreelancer,
    isOutsideFreelancer,
    isOutsideEmployer,
    setProject,
    setTasks,
    setTeam,
    onUpdateApplication,
    onUpdateInvitation,
}) => {

        const [canFindTalent, setCanFindTalent] = useState(false);
        const [canAddAdditionalTask, setCanAddAdditionalTask] = useState(false);

        const [applicationsGroupBy, setApplicationsGroupBy] = useState<EGroupBy>(EGroupBy.TALENT);
        const [invitationsGroupBy, setInvitationsGroupBy] = useState<EGroupBy>(EGroupBy.TALENT);

        const [applicationsMap, setApplicationsMap] = useState<{ [key: string]: Application }>({}); // application._id -> application
        const [invitationsMap, setInvitationsMap] = useState<{ [key: string]: Invitation }>({}); // invitation._id -> invitation
        const [applicantsMap, setApplicantsMap] = useState<{ [key: string]: IFreelancer }>({}); // applicant._id -> applicant
        const [invitedFreelancersMap, setInvitedFreelancersMap] = useState<{ [key: string]: IFreelancer }>({}); // invitedFreelancer._id -> invitedFreelancer

        // for faster grouping
        const [applicantsTaskIdsMap, setApplicantsTaskIdsMap] = useState<{ [key: string]: string[] }>({}); // applicant._id -> taskIds
        const [invitedFreelancersTaskIdsMap, setInvitedFreelancersTaskIdsMap] = useState<{ [key: string]: string[] }>({}); // invitedFreelancer._id -> taskIds
        const [taskApplicantsMap, setTaskApplicantsMap] = useState<{ [key: string]: IFreelancer[] }>({}); // taskId -> applicants
        const [taskInvitedFreelancersMap, setTaskInvitedFreelancersMap] = useState<{ [key: string]: IFreelancer[] }>({}); // taskId -> invitedFreelancers

        const [showAcceptApplicationLightbox, setShowAcceptApplicationLightbox] = useState<{ applicationId: string, task: ITask, freelancer: IFreelancer } | null>(null);
        const [showRejectApplicationLightbox, setShowRejectApplicationLightbox] = useState<{ applicationId: string, task: ITask, freelancer: IFreelancer } | null>(null);
        const [showWithdrawInvitationLightbox, setShowWithdrawInvitationLightbox] = useState<{ invitationId: string, task: ITask, freelancer: IFreelancer } | null>(null);
        const [showUndoWithdrawInvitationLightbox, setShowUndoWithdrawInvitationLightbox] = useState<{ invitationId: string, task: ITask, freelancer: IFreelancer } | null>(null);

        const {
            acceptApplication,
            rejectApplication,
        } = useApplications();

        const {
            withdrawInvitation,
        } = useInvitations();

        const { goToBrowseFreelancers, goToUserProfile } = useRouting();


        useEffect(() => {
            // can find talent
            if (isPm || isEmployer) {
                setCanFindTalent(true);
            } else {
                setCanFindTalent(false);
            }
        }, [isPm,
            isRoadmapVerificationPm,
            project,
            roadmapVerificationPm,
            projectManager,
            tasks,
        ]);

        useEffect(() => {

            const newApplicationsMap: { [key: string]: Application } = {};
            applications.forEach((application) => {
                newApplicationsMap[application._id!] = application;
            });
            setApplicationsMap(newApplicationsMap);

            const newInvitationsMap: { [key: string]: Invitation } = {};
            invitations.forEach((invitation) => {
                newInvitationsMap[invitation._id!] = invitation;
            });
            setInvitationsMap(newInvitationsMap);

            const newApplicantsMap: { [key: string]: IFreelancer } = {};
            applicants.forEach((applicant) => {
                newApplicantsMap[applicant._id!] = applicant;
            });
            setApplicantsMap(newApplicantsMap);

            const newInvitedFreelancersMap: { [key: string]: IFreelancer } = {};
            invitedFreelancers.forEach((invitedFreelancer) => {
                newInvitedFreelancersMap[invitedFreelancer._id!] = invitedFreelancer;
            });
            setInvitedFreelancersMap(newInvitedFreelancersMap);


            // prepare data for faster grouping
            // applicants -> taskIds
            const newApplicantsTaskIdsMap: { [key: string]: string[] } = {};
            applicants.forEach((applicant) => {
                const taskIds = applications
                    .filter((application) => application.freelancerId === applicant._id)
                    .map((application) => application.taskId)
                    .filter((id): id is string => id !== undefined);
                newApplicantsTaskIdsMap[applicant._id!] = taskIds;
            });
            setApplicantsTaskIdsMap(newApplicantsTaskIdsMap);

            // invitedFreelancers -> taskIds
            const newInvitedFreelancersTaskIdsMap: { [key: string]: string[] } = {};
            invitedFreelancers.forEach((invitedFreelancer) => {
                const taskIds = invitations
                    .filter((invitation) => invitation.freelancerId === invitedFreelancer._id)
                    .map((invitation) => invitation.taskId)
                    .filter((id): id is string => id !== undefined);
                newInvitedFreelancersTaskIdsMap[invitedFreelancer._id!] = taskIds;
            });
            setInvitedFreelancersTaskIdsMap(newInvitedFreelancersTaskIdsMap);

            // taskIds -> applicants
            const newTaskApplicantsMap: { [key: string]: IFreelancer[] } = {};
            tasks.forEach((task) => {
                const taskApplicants = applicants.filter((applicant) => newApplicantsTaskIdsMap[applicant._id!].includes(task._id!));
                newTaskApplicantsMap[task._id!] = taskApplicants;
            });
            setTaskApplicantsMap(newTaskApplicantsMap);

            // taskIds -> invitedFreelancers
            const newTaskInvitedFreelancersMap: { [key: string]: IFreelancer[] } = {};
            tasks.forEach((task) => {
                const taskInvitedFreelancers = invitedFreelancers.filter((invitedFreelancer) => newInvitedFreelancersTaskIdsMap[invitedFreelancer._id!].includes(task._id!));
                newTaskInvitedFreelancersMap[task._id!] = taskInvitedFreelancers;
            });
            setTaskInvitedFreelancersMap(newTaskInvitedFreelancersMap);

        }, [applications, invitations, applicants, invitedFreelancers]);
        return (

            <div className="flex flex-col gap-4">
                {/* Team Header */}
                <div className="-mt-2 -ml-4 flex flex-wrap items-center justify-between sm:flex-nowrap">
                    <div className="mt-2 ml-4">
                        <h3 className="text-2xl font-semibold text-gray-900">{isEmployer ? 'Your Team' : 'Team'}</h3>
                    </div>
                    <div className="mt-2 ml-4 shrink-0 space-x-2">


                        {canFindTalent &&
                            <Tooltip text="Find talent for this project.">
                                <OutlinedButton
                                    onClick={() => { goToBrowseFreelancers() }}
                                    theme='primary'
                                    icon={<IoSearchOutline />}
                                >Find Talent
                                </OutlinedButton>
                            </Tooltip>}

                    </div>
                </div>

                {/* Team */}
                <div className="overflow-hidden rounded-lg bg-white shadow-lg border border-gray-200">
                    {team.length === 0 ? <div className="flex flex-col items-center space-y-4 p-4">
                        <img
                            src="https://storage.googleapis.com/public-platform-assets/images/no_data.svg"
                            alt="No Data"
                            className="w-2/3 mx-auto"
                        />
                        <p className="w-full text-center mt-4 text-gray-600">At this moment, no one is part of your team.</p>

                    </div> : <div
                        className="mx-auto p-4 grid max-w-2xl grid-cols-1 gap-x-8 gap-y-16 sm:grid-cols-2 md:grid-cols-3 lg:mx-0 lg:max-w-none lg:grid-cols-4"
                    >
                        {team.map((freelancer) => (
                            <div key={freelancer._id} className="cursor-pointer transition-transform duration-300 hover:scale-105" onClick={() => { goToUserProfile(EUserType.FREELANCER, freelancer._id!) }}>
                                <div className="relative">
                                    <img alt="" src={freelancer.profilePictureUrl || DEFAULT_USER_PROFILE.DEFAULT_FREELANCER_PROFILE_PICTURE_MALE} className="aspect-square w-full rounded-2xl object-cover hover:shadow-lg" />
                                    <div className="absolute bottom-0 left-0 right-0 px-4 py-2 bg-black bg-opacity-40 rounded-b-2xl">
                                        <h3 className="text-sm font-semibold tracking-tight text-white">{getFreelancerName(freelancer)}</h3>
                                        <p className="text-sm text-white">{
                                            freelancer._id === projectManager?._id ? 'Project Manager'
                                                : freelancer._id === roadmapVerificationPm?._id ? 'Roadmap Expert' : 'Talent'}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>}
                </div>

                {/* Applications Header */}
                {(isPm || (isEmployer && (project.ownerManaged || !projectManager))) && <div className="-mt-2 -ml-4 flex flex-wrap items-center justify-between sm:flex-nowrap">
                    <div className="mt-2 ml-4">
                        <h3 className="text-2xl font-semibold text-gray-900">Applications</h3>
                    </div>
                    {/* <div className="mt-2 ml-4 shrink-0 space-x-2">
                        {canFindTalent &&
                            <div className="min-w-48">

                                <OutlinedDropdownMenu
                                    options={Object.values(EGroupBy)}
                                    onSelect={(option) => {
                                        setApplicationsGroupBy(option as EGroupBy);
                                    }}
                                    label="Group by"
                                    defaultValue={EGroupBy.TALENT}
                                />
                            </div>
                        }
                    </div> */}
                </div>}

                {/* Applications */}
                {(isPm || (isEmployer && (project.ownerManaged || !projectManager))) && <div className="p-4 overflow-hidden rounded-lg bg-white shadow-lg border border-gray-200">
                    {applications.length === 0 ? <div className="flex flex-col items-center space-y-4 ">
                        <img
                            src="https://storage.googleapis.com/public-platform-assets/images/no_data.svg"
                            alt="No Data"
                            className="w-2/3 mx-auto"
                        />
                        <p className="w-full text-center mt-4 text-gray-600">At this moment, there are no applications for this project.</p>
                    </div> :
                        // Group by talent
                        applicationsGroupBy === EGroupBy.TALENT ? (
                            <div className="mx-auto flex flex-col gap-4">
                                {applicants.map((applicant) => (
                                    <div key={applicant._id} className="cursor-pointer transition-transform duration-300 grid max-w-2xl grid-cols-1 gap-x-8 gap-y-16 sm:grid-cols-1 md:grid-cols-4 lg:mx-0 lg:max-w-none bg-gray-50 p-4 rounded-2xl">
                                        <div className='flex flex-col gap-1 items-end'>
                                            <div className='relative aspect-square w-full'>
                                                <img alt=""
                                                    src={applicant.profilePictureUrl || DEFAULT_USER_PROFILE.DEFAULT_FREELANCER_PROFILE_PICTURE_MALE}
                                                    className="w-full h-full rounded-2xl object-cover hover:shadow-lg"
                                                    onClick={() => { goToUserProfile(EUserType.FREELANCER, applicant._id!) }} />
                                                <div className="absolute bottom-0 left-0 right-0 px-4 py-2 bg-black bg-opacity-40 rounded-b-2xl">
                                                    <h3 className="text-sm font-semibold tracking-tight text-white">{getFreelancerName(applicant)}</h3>
                                                    <p className="text-sm text-white line-clamp-1">{applicant.roles?.map((role) => UserEnumTextMapper.freelancerRole(role)).join(', ') || 'Talent'}</p>
                                                </div>
                                            </div>
                                            <ChatNowButton user={applicant} />
                                        </div>


                                        <div className='flex flex-col gap-2 md:col-span-3'>
                                            {applicantsTaskIdsMap[applicant._id!]?.map((taskId) => (
                                                <div key={taskId} className='rounded-lg p-2 border border-white bg-white hover:border-gray-200 transition-all duration-300'>
                                                    <div className='flex flex-row gap-2'>
                                                        <p className='text-xs font-bold text-gray-400'>{dateToShortReadable(applications.find((application) => application.taskId === taskId && application.freelancerId === applicant._id)?._createdAt)}</p>
                                                        <p className='text-xs font-bold text-gray-400'>[{ApplicationEnumTextMapper.applicationStatus(applications.find((application) => application.taskId === taskId && application.freelancerId === applicant._id)?.status!)}]</p>
                                                    </div>
                                                    <div className='flex flex-col md:flex-row md:items-center'>
                                                        <div className='flex flex-col md:flex-row gap-2'>
                                                            <p>{tasks.find((task) => task._id === taskId)?.projectRank}. {tasks.find((task) => task._id === taskId)?.title}</p>
                                                            <TaskStatusBatch status={tasks.find((task) => task._id === taskId)?.currentStatus || ETaskStatus.DRAFT} />
                                                        </div>
                                                        <div className='flex-1' />
                                                        {(tasks.find((task) => task._id === taskId)?.currentStatus || 0) <= ETaskStatus.WAIT_FOR_PAYMENT
                                                            ?
                                                            // in draft, not approved or not paid
                                                            <Tooltip text="This task must be approved and paid before a talent can be assigned."><p>Not yet paid by client</p></Tooltip>
                                                            : (tasks.find((task) => task._id === taskId)?.currentStatus === ETaskStatus.REASSIGN ||
                                                                tasks.find((task) => task._id === taskId)?.currentStatus === ETaskStatus.WAIT_FOR_FREELANCER)
                                                                ?
                                                                // task can potentially be assigned to this talent
                                                                <div className=''>

                                                                    {applications.find((application) => application.taskId === taskId && application.freelancerId === applicant._id)?.status === EApplicationStatus.REJECTED
                                                                        ? <Tooltip text="Application Rejected. If you want this talent to work on this task, you can send him an invitation."><p>Application Rejected</p></Tooltip>
                                                                        : (applications.find((application) => application.taskId === taskId && application.freelancerId === applicant._id)?.status === EApplicationStatus.ACCEPTED_BUT_REFUSED)
                                                                            ? <Tooltip text="This talent has refused working on this task after being accepted."><p>Refused</p></Tooltip>
                                                                            : (applications.find((application) => application.taskId === taskId && application.freelancerId === applicant._id)?.status === EApplicationStatus.ACCEPTED
                                                                                ? <Tooltip text="This talent has been accepted for this task. Waiting for confirmation."><p>Accepted</p></Tooltip>
                                                                                : (applications.find((application) => application.taskId === taskId && application.freelancerId === applicant._id)?.status === EApplicationStatus.ACCEPTED_AND_CONFIRMED)
                                                                                    ? <Tooltip text="Talent has confirmed working on this task."><p>Accepted & Confirmed</p></Tooltip>
                                                                                    : (applications.find((application) => application.taskId === taskId && application.freelancerId === applicant._id)?.status === EApplicationStatus.EXPIRED)
                                                                                        ? <Tooltip text="This application has expired."><p>Application Expired</p></Tooltip>
                                                                                        : (applications.find((application) => application.taskId === taskId && application.freelancerId === applicant._id)?.status === EApplicationStatus.WITHDRAWN)
                                                                                            ? <Tooltip text="The talent has withdrawn his application to this task."><p>Application Withdrawn</p></Tooltip>
                                                                                            : <div className='flex flex-row gap-2'>
                                                                                                <TextButton
                                                                                                    size='small'
                                                                                                    theme='danger'
                                                                                                    onClick={() => {
                                                                                                        setShowRejectApplicationLightbox({
                                                                                                            applicationId: applications.find((application) => application.taskId === taskId && application.freelancerId === applicant._id)?._id!,
                                                                                                            task: tasks.find((task) => task._id === taskId)!,
                                                                                                            freelancer: applicant
                                                                                                        })
                                                                                                    }}
                                                                                                >Reject</TextButton>
                                                                                                <TextButton
                                                                                                    size='small'
                                                                                                    theme='primary'
                                                                                                    onClick={() => {
                                                                                                        setShowAcceptApplicationLightbox({
                                                                                                            applicationId: applications.find((application) => application.taskId === taskId && application.freelancerId === applicant._id)?._id!,
                                                                                                            task: tasks.find((task) => task._id === taskId)!,
                                                                                                            freelancer: applicant
                                                                                                        })
                                                                                                    }}
                                                                                                >Accept</TextButton>
                                                                                            </div>)}
                                                                </div>
                                                                :
                                                                // task currently in progress 
                                                                ((tasks.find((task) => task._id === taskId)?.currentStatus || 0) >= ETaskStatus.IN_PROGRESS &&
                                                                    (tasks.find((task) => task._id === taskId)?.currentStatus || Infinity) < ETaskStatus.COMPLETED)
                                                                    ? <Tooltip text="This task is currently in progress. Applications for this task cannot be accepted or rejected."><p>Task in progress</p></Tooltip>
                                                                    : (tasks.find((task) => task._id === taskId)?.currentStatus || 0) === ETaskStatus.COMPLETED
                                                                        ? <Tooltip text="This task is already completed. Applications for this task cannot be accepted or rejected anymore."><p>Task Completed</p></Tooltip>
                                                                        : (tasks.find((task) => task._id === taskId)?.currentStatus || 0) === ETaskStatus.CANCELLED
                                                                            ? <Tooltip text="This task is cancelled. Applications for this task cannot be accepted or rejected anymore."><p>Task Cancelled</p></Tooltip>
                                                                            : <div></div>
                                                        }
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}


                            </div>
                        )
                            // Group by task 
                            : applicationsGroupBy === EGroupBy.TASK ? (
                                <div className="mx-auto p-4 grid max-w-2xl grid-cols-1 gap-x-8 gap-y-16 sm:grid-cols-2 lg:mx-0 lg:max-w-none lg:grid-cols-3">
                                    {tasks.map((task) => (
                                        <div key={task._id} className="cursor-pointer">
                                            <h3 className="mt-6 text-lg/8 font-semibold tracking-tight text-gray-900">{task.title}</h3>
                                            <p className="text-base/7 text-gray-600">{task.description}</p>
                                        </div>
                                    ))}
                                </div>
                            ) : null}
                </div>}

                {/* Invitations Header */}
                {(isPm || (isEmployer && project.ownerManaged || !projectManager)) && <div className="-mt-2 -ml-4 flex flex-wrap items-center justify-between sm:flex-nowrap">
                    <div className="mt-2 ml-4">
                        <h3 className="text-2xl font-semibold text-gray-900">Invitations</h3>
                    </div>
                    {/* <div className="mt-2 ml-4 shrink-0 space-x-2">
                    {canFindTalent &&
                        <Tooltip text="Find talent for this project.">
                            <OutlinedButton
                                onClick={() => { goToBrowseFreelancers() }}
                                theme='primary'
                                icon={<IoSearchOutline />}
                            >Find Talent
                            </OutlinedButton>
                        </Tooltip>}
                </div> */}
                </div>}

                {/* Invitations */}
                {(isPm || (isEmployer && (project.ownerManaged || !projectManager))) && <div className="p-4 overflow-hidden rounded-lg bg-white shadow-lg border border-gray-200">
                    {invitations.length === 0 ? <div className="flex flex-col items-center space-y-4 ">
                        <img
                            src="https://storage.googleapis.com/public-platform-assets/images/no_data.svg"
                            alt="No Data"
                            className="w-2/3 mx-auto"
                        />
                        <p className="w-full text-center mt-4 text-gray-600">At this moment, there are no invitations for this project.</p>
                    </div> : <div>
                        {invitationsGroupBy === EGroupBy.TALENT ? (
                            <div className="mx-auto flex flex-col gap-4">
                                {invitedFreelancers.map((invitedFreelancer) => (
                                    <div key={invitedFreelancer._id} className="cursor-pointer transition-transform duration-300 grid max-w-2xl grid-cols-1 gap-x-8 gap-y-16 sm:grid-cols-1 md:grid-cols-4 lg:mx-0 lg:max-w-none bg-gray-50 p-4 rounded-2xl">
                                        <div className='flex flex-col gap-1 items-end'>
                                            <div className='relative aspect-square w-full'>
                                                <img alt=""
                                                    src={invitedFreelancer.profilePictureUrl || DEFAULT_USER_PROFILE.DEFAULT_FREELANCER_PROFILE_PICTURE_MALE}
                                                    className="w-full h-full rounded-2xl object-cover hover:shadow-lg"
                                                    onClick={() => { goToUserProfile(EUserType.FREELANCER, invitedFreelancer._id!) }} />
                                                <div className="absolute bottom-0 left-0 right-0 px-4 py-2 bg-black bg-opacity-40 rounded-b-2xl">
                                                    <h3 className="text-sm font-semibold tracking-tight text-white">{getFreelancerName(invitedFreelancer)}</h3>
                                                    <p className="text-sm text-white line-clamp-1">{invitedFreelancer.roles?.map((role) => UserEnumTextMapper.freelancerRole(role)).join(', ') || 'Talent'}</p>
                                                </div>
                                            </div>
                                            <ChatNowButton user={invitedFreelancer} />
                                        </div>



                                        <div className='flex flex-col gap-2 md:col-span-3'>
                                            {invitedFreelancersTaskIdsMap[invitedFreelancer._id!]?.map((taskId) => (
                                                <div key={taskId} className='rounded-lg p-2 border border-white bg-white hover:border-gray-200 transition-all duration-300'>
                                                    <div className='flex flex-row gap-2'>
                                                        <p className='text-xs font-bold text-gray-400'>{dateToShortReadable(invitations.find((invitation) => invitation.taskId === taskId && invitation.freelancerId === invitedFreelancer._id)?._createdAt)}</p>
                                                        <p className='text-xs font-bold text-gray-400'>[{InvitationEnumTextMapper.invitationStatus(invitations.find((invitation) => invitation.taskId === taskId && invitation.freelancerId === invitedFreelancer._id)?.status!)}]</p>
                                                    </div>
                                                    <div className='flex flex-col md:flex-row  md:items-center '>
                                                        <div className='flex flex-col md:flex-row gap-2'>
                                                            <p>{tasks.find((task) => task._id === taskId)?.projectRank}. {tasks.find((task) => task._id === taskId)?.title}</p>
                                                            <TaskStatusBatch status={tasks.find((task) => task._id === taskId)?.currentStatus || ETaskStatus.DRAFT} />
                                                        </div>
                                                        <div className='flex-1' />
                                                        {(tasks.find((task) => task._id === taskId)?.currentStatus || 0) <= ETaskStatus.WAIT_FOR_PAYMENT
                                                            ?
                                                            // in draft, not approved or not paid
                                                            <Tooltip text="This task must be approved and paid before a talent can be assigned."><p>Not yet paid by client</p></Tooltip>
                                                            : (tasks.find((task) => task._id === taskId)?.currentStatus === ETaskStatus.REASSIGN ||
                                                                tasks.find((task) => task._id === taskId)?.currentStatus === ETaskStatus.WAIT_FOR_FREELANCER)
                                                                ?
                                                                // task can potentially be assigned to this talent
                                                                <div className=''>

                                                                    {invitations.find((invitation) => invitation.taskId === taskId && invitation.freelancerId === invitedFreelancer._id)?.status === EInvitationStatus.ACCEPTED
                                                                        ? <Tooltip text="Your invitation has been accepted by the talent."><p>Accepted</p></Tooltip>
                                                                        : invitations.find((invitation) => invitation.taskId === taskId && invitation.freelancerId === invitedFreelancer._id)?.status === EInvitationStatus.REJECTED
                                                                            ? <Tooltip text="Your invitation has been rejected by the talent."><p>Rejected</p></Tooltip>
                                                                            : invitations.find((invitation) => invitation.taskId === taskId && invitation.freelancerId === invitedFreelancer._id)?.status === EInvitationStatus.WITHDRAWN
                                                                                ? <div className='flex flex-row gap-2'>
                                                                                    <TextButton
                                                                                        size='small'
                                                                                        theme='primary'
                                                                                        onClick={() => {
                                                                                            setShowUndoWithdrawInvitationLightbox({
                                                                                                invitationId: invitations.find((invitation) => invitation.taskId === taskId && invitation.freelancerId === invitedFreelancer._id)?._id!,
                                                                                                task: tasks.find((task) => task._id === taskId)!,
                                                                                                freelancer: invitedFreelancer
                                                                                            })
                                                                                        }}
                                                                                    >Undo Withdrawal</TextButton>
                                                                                </div>
                                                                                : invitations.find((invitation) => invitation.taskId === taskId && invitation.freelancerId === invitedFreelancer._id)?.status === EInvitationStatus.EXPIRED
                                                                                    ? <Tooltip text="Your invitation has expired."><p>Expired</p></Tooltip>
                                                                                    : invitations.find((invitation) => invitation.taskId === taskId && invitation.freelancerId === invitedFreelancer._id)?.status === EInvitationStatus.PENDING
                                                                                        ? <div className='flex flex-row gap-2'>
                                                                                            <TextButton
                                                                                                size='small'
                                                                                                theme='danger'
                                                                                                onClick={() => {
                                                                                                    setShowWithdrawInvitationLightbox({
                                                                                                        invitationId: invitations.find((invitation) => invitation.taskId === taskId && invitation.freelancerId === invitedFreelancer._id)?._id!,
                                                                                                        task: tasks.find((task) => task._id === taskId)!,
                                                                                                        freelancer: invitedFreelancer
                                                                                                    })
                                                                                                }}
                                                                                            >Withdraw</TextButton>
                                                                                        </div> : <div>{invitations.find((invitation) => invitation.taskId === taskId && invitation.freelancerId === invitedFreelancer._id)?.status === EInvitationStatus.CANCELLED ? <div>Cancelled</div> : <div>Not Cancelled</div>}</div>}
                                                                </div> :
                                                                // task currently in progress 
                                                                ((tasks.find((task) => task._id === taskId)?.currentStatus || 0) >= ETaskStatus.IN_PROGRESS &&
                                                                    (tasks.find((task) => task._id === taskId)?.currentStatus || Infinity) < ETaskStatus.COMPLETED)
                                                                    ? <Tooltip text="This task is currently in progress. Applications for this task cannot be accepted or rejected."><p>Task in progress</p></Tooltip>
                                                                    : (tasks.find((task) => task._id === taskId)?.currentStatus || 0) === ETaskStatus.COMPLETED
                                                                        ? <Tooltip text="This task is already completed. Applications for this task cannot be accepted or rejected anymore."><p>Task Completed</p></Tooltip>
                                                                        : (tasks.find((task) => task._id === taskId)?.currentStatus || 0) === ETaskStatus.CANCELLED
                                                                            ? <Tooltip text="This task is cancelled. Applications for this task cannot be accepted or rejected anymore."><p>Task Cancelled</p></Tooltip>
                                                                            : <div></div>
                                                        }
                                                    </div>
                                                </div>
                                            ))}

                                        </div>
                                    </div>
                                ))}


                            </div>
                        )
                            // Group by task 
                            : applicationsGroupBy === EGroupBy.TASK ? (
                                <div className="mx-auto p-4 grid max-w-2xl grid-cols-1 gap-x-8 gap-y-16 sm:grid-cols-2 lg:mx-0 lg:max-w-none lg:grid-cols-3">
                                    {tasks.map((task) => (
                                        <div key={task._id} className="cursor-pointer">
                                            <h3 className="mt-6 text-lg/8 font-semibold tracking-tight text-gray-900">{task.title}</h3>
                                            <p className="text-base/7 text-gray-600">{task.description}</p>
                                        </div>
                                    ))}
                                </div>
                            ) : null}
                    </div>}
                </div>}

                {showAcceptApplicationLightbox && <ApplicationAcceptLightbox
                    applicationId={showAcceptApplicationLightbox.applicationId}
                    task={showAcceptApplicationLightbox.task}
                    freelancer={showAcceptApplicationLightbox.freelancer}
                    onClose={() => setShowAcceptApplicationLightbox(null)}
                    onUpdateApplication={onUpdateApplication}
                />}

                {showRejectApplicationLightbox && <ApplicationRejectLightbox
                    applicationId={showRejectApplicationLightbox.applicationId}
                    task={showRejectApplicationLightbox.task}
                    freelancer={showRejectApplicationLightbox.freelancer}
                    onClose={() => setShowRejectApplicationLightbox(null)}
                    onUpdateApplication={onUpdateApplication}
                />}

                {showWithdrawInvitationLightbox && <InvitationWithdrawLightbox
                    invitationId={showWithdrawInvitationLightbox.invitationId}
                    task={showWithdrawInvitationLightbox.task}
                    freelancer={showWithdrawInvitationLightbox.freelancer}
                    onClose={() => setShowWithdrawInvitationLightbox(null)}
                    onUpdateInvitation={onUpdateInvitation}
                />}

                {showUndoWithdrawInvitationLightbox && <InvitationUndoWithdrawLightbox
                    invitationId={showUndoWithdrawInvitationLightbox.invitationId}
                    task={showUndoWithdrawInvitationLightbox.task}
                    freelancer={showUndoWithdrawInvitationLightbox.freelancer}
                    onClose={() => setShowUndoWithdrawInvitationLightbox(null)}
                    onUpdateInvitation={onUpdateInvitation}
                />}
            </div >


        )
    }